import React, { useState, useRef, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import './AutocompleteSelect.css';

/**
 * AutocompleteSelect - Composant de sélection avec autocomplétion et création automatique
 * 
 * @param {Array} options - Liste des options existantes [{id, nom}]
 * @param {string} value - ID de l'option sélectionnée ou nom saisi
 * @param {Function} onChange - Callback appelé quand la valeur change (id ou nom)
 * @param {Function} onCreateNew - Callback appelé pour créer un nouveau client/fournisseur
 * @param {string} placeholder - Texte du placeholder
 * @param {string} label - Label du champ
 * @param {boolean} disabled - Désactiver le champ
 * @param {string} createText - Texte pour la création (ex: "Créer le fournisseur")
 */
const AutocompleteSelect = ({
  options = [],
  value,
  onChange,
  onCreateNew,
  placeholder = "Saisissez ou sélectionnez",
  label,
  disabled = false,
  createText = "Créer"
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const wrapperRef = useRef(null);

  // Initialiser inputValue si value est un ID
  useEffect(() => {
    if (value && typeof value === 'number') {
      const selected = options.find(opt => opt.id === value);
      if (selected) {
        setInputValue(selected.nom);
        setSelectedId(selected.id);
      }
    } else if (value && typeof value === 'string') {
      setInputValue(value);
      setSelectedId(null);
    }
  }, [value, options]);

  // Fermer les suggestions si clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtrer les options pendant la frappe
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedId(null);

    if (newValue.trim() === '') {
      setFilteredOptions([]);
      setShowSuggestions(false);
      onChange(null);
      return;
    }

    // Filtrer les options
    // CORRECTION : archived peut etre undefined pour les anciennes donnees.
    // On exclut seulement les elements explicitement archives.
    const filtered = options.filter(option =>
      option.nom.toLowerCase().includes(newValue.toLowerCase()) &&
      option.archived !== true
    );

    setFilteredOptions(filtered);
    setShowSuggestions(true);

    // Si aucune correspondance exacte, on garde le texte pour création
    onChange(newValue);
  };

  // Sélectionner une option existante
  const handleSelectOption = (option) => {
    setInputValue(option.nom);
    setSelectedId(option.id);
    setShowSuggestions(false);
    onChange(option.id);
  };

  // Créer un nouveau client/fournisseur
  const handleCreateNew = async () => {
    if (!inputValue.trim()) return;

    // Vérifier si le nom existe déjà (insensible à la casse)
    const exists = options.find(
      opt => opt.nom.toLowerCase() === inputValue.trim().toLowerCase()
    );

    if (exists) {
      // Si existe déjà, sélectionner
      handleSelectOption(exists);
      return;
    }

    // Créer le nouveau
    if (onCreateNew) {
      try {
        const newItem = await onCreateNew(inputValue.trim());
        if (newItem && newItem.id) {
          setSelectedId(newItem.id);
          setInputValue(newItem.nom);
          setShowSuggestions(false);
          onChange(newItem.id);
        }
      } catch (error) {
        console.error('Erreur création:', error);
      }
    }
  };

  // Focus sur l'input
  const handleFocus = () => {
    if (inputValue && !selectedId) {
      const filtered = options.filter(option =>
        option.nom.toLowerCase().includes(inputValue.toLowerCase()) &&
        option.archived !== true
      );
      setFilteredOptions(filtered);
      setShowSuggestions(true);
    }
  };

  // Gérer la touche Entrée
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length === 1) {
        handleSelectOption(filteredOptions[0]);
      } else if (filteredOptions.length === 0 && inputValue.trim()) {
        handleCreateNew();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const exactMatch = options.find(
    opt => opt.nom.toLowerCase() === inputValue.toLowerCase()
  );

  const showCreateButton = inputValue.trim() && 
                          !selectedId && 
                          !exactMatch && 
                          showSuggestions;

  return (
    <div ref={wrapperRef} className="autocomplete-select-wrapper">
      {label && <Form.Label>{label}</Form.Label>}
      <div className="autocomplete-input-container">
        <Form.Control
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="rounded rounded-pill autocomplete-input"
          autoComplete="off"
        />
        {selectedId && (
          <span className="selected-indicator">✓</span>
        )}
      </div>

      {showSuggestions && (filteredOptions.length > 0 || showCreateButton) && (
        <div className="autocomplete-suggestions">
          {filteredOptions.length > 0 && (
            <>
              <div className="suggestions-header">Suggestions</div>
              {filteredOptions.map((option) => (
                <div
                  key={option.id}
                  className="suggestion-item"
                  onClick={() => handleSelectOption(option)}
                >
                  <span className="suggestion-name">{option.nom}</span>
                  {option.telephone && (
                    <span className="suggestion-info">{option.telephone}</span>
                  )}
                </div>
              ))}
            </>
          )}

          {showCreateButton && (
            <>
              {filteredOptions.length > 0 && <div className="suggestions-divider" />}
              <div
                className="suggestion-item create-new"
                onClick={handleCreateNew}
              >
                <span className="create-icon">+</span>
                <span className="create-text">
                  {createText} &ldquo;<strong>{inputValue}</strong>&rdquo;
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {!showSuggestions && inputValue && !selectedId && (
        <small className="text-muted">
          Appuyez sur Entrée pour créer &ldquo;{inputValue}&rdquo;
        </small>
      )}
    </div>
  );
};

export default AutocompleteSelect;
